import React from 'react';
import { useToastState } from '../../store/useAppStore';
import { Toast as ToastType } from '../../store/types';

const Toast: React.FC<{ toast: ToastType; onClose: (id: string) => void }> = ({ 
  toast, 
  onClose 
}) => {
  const getToastStyles = (type: ToastType['type']) => {
    const baseStyles = {
      padding: 'var(--wa-space-4)',
      marginBottom: 'var(--wa-space-3)',
      borderRadius: 'var(--wa-rounded-lg)',
      boxShadow: 'var(--wa-shadow-lg)',
      borderLeft: '4px solid',
      transition: 'all var(--wa-duration-normal) var(--wa-ease-out)',
      transform: 'scale(1)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      position: 'relative' as const,
      overflow: 'hidden' as const
    };
    
    switch (type) {
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderLeftColor: 'var(--wa-success-500)',
          color: 'var(--wa-success-800)',
          borderColor: 'rgba(34, 197, 94, 0.2)'
        };
      case 'error':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderLeftColor: 'var(--wa-error-500)',
          color: 'var(--wa-error-800)',
          borderColor: 'rgba(239, 68, 68, 0.2)'
        };
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderLeftColor: 'var(--wa-warning-500)',
          color: 'var(--wa-warning-800)',
          borderColor: 'rgba(245, 158, 11, 0.2)'
        };
      case 'info':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderLeftColor: 'var(--wa-primary-500)',
          color: 'var(--wa-primary-800)',
          borderColor: 'rgba(59, 130, 246, 0.2)'
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: 'rgba(107, 114, 128, 0.1)',
          borderLeftColor: 'var(--wa-gray-500)',
          color: 'var(--wa-gray-800)',
          borderColor: 'rgba(107, 114, 128, 0.2)'
        };
    }
  };

  const getIcon = (type: ToastType['type']) => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
        return (
          <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getToastClasses = (type: ToastType['type']) => {
    switch (type) {
      case 'success':
        return 'wa-toast-success';
      case 'error':
        return 'wa-toast-error';
      case 'warning':
        return 'wa-toast-warning';
      case 'info':
        return 'wa-toast-info';
      default:
        return '';
    }
  };

  return (
    <div 
      data-toast 
      className={getToastClasses(toast.type)}
      style={getToastStyles(toast.type)}
    >
      {/* Shimmer effect on hover */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
          transition: 'left var(--wa-duration-slow) var(--wa-ease-out)',
          pointerEvents: 'none'
        }}
      />
      
      <div className="flex items-start">
        {getIcon(toast.type)}
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 className="wa-body-small" style={{ fontWeight: 'var(--wa-font-semibold)', marginBottom: 'var(--wa-space-1)' }}>
            {toast.title}
          </h4>
          {toast.message && (
            <p className="wa-body-small" style={{ opacity: 0.9 }}>
              {toast.message}
            </p>
          )}
          
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="wa-transition-colors"
              style={{
                marginTop: 'var(--wa-space-2)',
                fontSize: 'var(--wa-text-sm)',
                fontWeight: 'var(--wa-font-medium)',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                padding: 0
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        <button
          onClick={() => onClose(toast.id)}
          className="wa-transition-colors wa-hover-scale"
          style={{
            marginLeft: 'var(--wa-space-3)',
            flexShrink: 0,
            padding: 'var(--wa-space-1)',
            borderRadius: 'var(--wa-rounded-full)',
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            opacity: 0.7
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.opacity = '0.7';
          }}
          aria-label="Fechar notificação"
        >
          <svg 
            style={{ width: 'var(--wa-space-4)', height: 'var(--wa-space-4)' }} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      
      {/* Auto-dismiss progress bar (if duration is set) */}
      {toast.duration && (
        <div 
          className="wa-toast-progress"
          style={{
            animationDuration: `${toast.duration}ms`
          }}
        />
      )}
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts, hideToast } = useToastState();

  if (toasts.length === 0) return null;

  return (
    <div 
      className="wa-toast-container"
      style={{
        position: 'fixed',
        top: '88px', // Height of navbar (72px) + spacing (16px)
        right: 'var(--wa-space-4)',
        zIndex: 'var(--wa-z-toast)', // Use design system z-index (1080)
        maxWidth: '24rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--wa-space-2)',
        pointerEvents: 'none' // Allow clicks through the container
      }}
    >
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="wa-animate-slide-left"
          style={{
            pointerEvents: 'auto', // Re-enable clicks on individual toasts
            animationDelay: `${index * 100}ms` // Stagger animation
          }}
        >
          <Toast 
            toast={toast} 
            onClose={hideToast} 
          />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;